// =============================================================================
// MeuDriver — RecurringBookingForm (Batch S1)
// Formulário para criação de série de aulas recorrentes.
// Usado no BookingFlow como modo alternativo ("Agendar recorrente").
// =============================================================================

import { useState } from 'react'
import { RepeatIcon, Loader2, CalendarDays } from 'lucide-react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { cn } from '@/lib/utils'
import { useCreateBookingSeries } from '@/hooks/useRecurringBookings'
import type { RecurrencePattern } from '@/hooks/useRecurringBookings'

// ─── Constantes ───────────────────────────────────────────────────────────────

const WEEK_DAYS = [
  { value: 0, label: 'Dom' },
  { value: 1, label: 'Seg' },
  { value: 2, label: 'Ter' },
  { value: 3, label: 'Qua' },
  { value: 4, label: 'Qui' },
  { value: 5, label: 'Sex' },
  { value: 6, label: 'Sáb' },
] as const

const WEEK_OPTIONS = [2, 4, 6, 8] as const

// ─── Props ────────────────────────────────────────────────────────────────────

interface Props {
  instructorId: string
  bookingType: string
  onSuccess?: (seriesId: string, bookingsCreated: number) => void
  onCancel?: () => void
}

// ─── Component ────────────────────────────────────────────────────────────────

export function RecurringBookingForm({ instructorId, bookingType, onSuccess, onCancel }: Props) {
  const [selectedDays, setSelectedDays]       = useState<number[]>([])
  const [time, setTime]                       = useState('09:00')
  const [durationWeeks, setDurationWeeks]     = useState<number>(4)
  const [notes, setNotes]                     = useState('')

  const createSeries = useCreateBookingSeries()

  function toggleDay(day: number) {
    setSelectedDays((prev) =>
      prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day],
    )
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()

    if (selectedDays.length === 0) {
      toast.error('Selecione pelo menos um dia da semana.')
      return
    }
    if (!time) {
      toast.error('Informe o horário das aulas.')
      return
    }

    const recurrence: RecurrencePattern = {
      days:           selectedDays.sort((a, b) => a - b),
      time,
      duration_weeks: durationWeeks,
    }

    try {
      const result = await createSeries.mutateAsync({
        instructor_id: instructorId,
        recurrence,
        booking_type:  bookingType,
        notes:         notes.trim() || undefined,
      })
      toast.success(
        `Série criada com sucesso! ${result.bookings_created} aula${result.bookings_created !== 1 ? 's' : ''} agendada${result.bookings_created !== 1 ? 's' : ''}.`,
        { duration: 5000 },
      )
      onSuccess?.(result.series_id, result.bookings_created)
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Erro ao criar série de aulas.'
      toast.error(msg)
    }
  }

  const estimatedLessons = selectedDays.length * durationWeeks

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-2">
        <RepeatIcon className="h-5 w-5 text-brand-600" />
        <h3 className="text-base font-semibold text-gray-800">Aulas recorrentes</h3>
      </div>

      {/* Dias da semana */}
      <div className="space-y-2">
        <Label className="text-sm font-medium text-gray-700">
          Dias da semana
        </Label>
        <div className="flex flex-wrap gap-2">
          {WEEK_DAYS.map(({ value, label }) => {
            const isSelected = selectedDays.includes(value)
            return (
              <button
                key={value}
                type="button"
                onClick={() => toggleDay(value)}
                data-testid={`weekday-${value}`}
                className={cn(
                  'h-10 w-12 rounded-xl border-2 text-sm font-semibold transition-all',
                  isSelected
                    ? 'border-brand-500 bg-brand-50 text-brand-700 shadow-sm'
                    : 'border-gray-200 bg-white text-gray-500 hover:border-gray-300',
                )}
              >
                {label}
              </button>
            )
          })}
        </div>
        <p className="text-xs text-muted-foreground">
          Selecione os dias em que as aulas vão ocorrer.
        </p>
      </div>

      {/* Horário */}
      <div className="space-y-2">
        <Label htmlFor="recurring-time" className="text-sm font-medium text-gray-700">
          Horário das aulas
        </Label>
        <Input
          id="recurring-time"
          type="time"
          value={time}
          onChange={(e) => setTime(e.target.value)}
          className="w-36"
          required
        />
      </div>

      {/* Número de semanas */}
      <div className="space-y-2">
        <Label className="text-sm font-medium text-gray-700">
          Duração da série
        </Label>
        <div className="flex flex-wrap gap-2">
          {WEEK_OPTIONS.map((w) => (
            <button
              key={w}
              type="button"
              onClick={() => setDurationWeeks(w)}
              data-testid={`weeks-${w}`}
              className={cn(
                'rounded-xl border-2 px-4 py-2 text-sm font-semibold transition-all',
                durationWeeks === w
                  ? 'border-brand-500 bg-brand-50 text-brand-700 shadow-sm'
                  : 'border-gray-200 bg-white text-gray-500 hover:border-gray-300',
              )}
            >
              {w} semanas
            </button>
          ))}
        </div>
      </div>

      {/* Resumo estimado */}
      {selectedDays.length > 0 && (
        <div className="rounded-xl border border-brand-100 bg-brand-50 p-4">
          <div className="flex items-center gap-2 text-sm text-brand-800">
            <CalendarDays className="h-4 w-4 shrink-0" />
            <span>
              Estimativa:{' '}
              <strong>{estimatedLessons} aula{estimatedLessons !== 1 ? 's' : ''}</strong>{' '}
              ao longo de <strong>{durationWeeks} semanas</strong>
            </span>
          </div>
        </div>
      )}

      {/* Observações */}
      <div className="space-y-2">
        <Label htmlFor="recurring-notes" className="text-sm font-medium text-gray-700">
          Observações{' '}
          <span className="text-xs font-normal text-muted-foreground">(opcional)</span>
        </Label>
        <Textarea
          id="recurring-notes"
          placeholder="Algum detalhe para o instrutor sobre a série de aulas..."
          rows={2}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          maxLength={500}
        />
      </div>

      {/* Actions */}
      <div className="flex gap-3 pt-1">
        <Button
          type="submit"
          disabled={createSeries.isPending || selectedDays.length === 0}
          className="gap-2"
          data-testid="submit-recurring-booking"
        >
          {createSeries.isPending
            ? <><Loader2 className="h-4 w-4 animate-spin" />Criando série...</>
            : <><RepeatIcon className="h-4 w-4" />Criar série de aulas</>
          }
        </Button>
        {onCancel && (
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancelar
          </Button>
        )}
      </div>
    </form>
  )
}
